/**
 * Domain Types
 *
 * Canonical shapes used by both local and cloud repositories.
 * Both repositories MUST map their internal storage to these types.
 */

// Re-export StepType from database for consistency
export type { StepType, StoryStatus } from './database'

/**
 * Unified story record - returned by both repositories
 */
export interface StoryRecord {
  id: string
  childId: string
  title: string
  coverEmoji: string
  status: 'in_progress' | 'completed' | 'archived'
  wordCount: number
  createdAt: string  // ISO string
  updatedAt: string  // ISO string
}

/**
 * Unified step record - returned by both repositories
 */
export interface StepRecord {
  id: string
  storyId: string
  stepType: 'beginning' | 'problem' | 'adventure' | 'climax' | 'resolution'
  stepOrder: number
  content: string
  wordCount: number
  promptHint: string | null
  updatedAt: string  // ISO string
}

/**
 * Story with all steps combined
 */
export interface StoryWithSteps {
  story: StoryRecord
  steps: StepRecord[]
}

/**
 * Step order mapping for consistency
 */
export const STEP_ORDER: Record<string, number> = {
  beginning: 1,
  problem: 2,
  adventure: 3,
  climax: 4,
  resolution: 5,
}

/**
 * Default prompt hints for each step
 */
export const STEP_PROMPTS: Record<string, string> = {
  beginning: 'How does your story start? Introduce your characters and setting.',
  problem: 'What problem or challenge does your hero face?',
  adventure: 'What happens as your hero tries to solve the problem?',
  climax: 'What is the most exciting moment? How does everything come together?',
  resolution: 'How does the story end? What did your hero learn?',
}

/**
 * Repository operation result - consistent error handling
 */
export type RepositoryResult<T> =
  | { success: true; data: T }
  | { success: false; error: string; code?: RepositoryErrorCode }

/**
 * Error codes for repository operations
 */
export type RepositoryErrorCode =
  | 'NOT_FOUND'
  | 'LIMIT_REACHED'
  | 'READ_ONLY'
  | 'NETWORK_ERROR'
  | 'VALIDATION_ERROR'
  | 'CONFLICT'
  | 'UNKNOWN'

/**
 * Calculate word count from content
 */
export function calculateWordCount(content: string): number {
  return content.trim().split(/\s+/).filter(Boolean).length
}

// =============================================================================
// Structure Analyzer Types
// =============================================================================

/**
 * Story structure elements that can be detected
 */
export type StoryElement =
  | 'problem'
  | 'goal'
  | 'obstacle'
  | 'turning_point'
  | 'resolution'
  | 'change'

/**
 * Detection status - 3-state instead of boolean
 * - missing: element not detected
 * - maybe: element possibly detected (low confidence)
 * - found: element clearly detected (high confidence)
 */
export type DetectionStatus = 'missing' | 'maybe' | 'found'

/**
 * Current analyzer version for compatibility tracking
 */
export const ANALYZER_VERSION = 1

/**
 * Result of analyzing a story's structure
 */
export interface StructureAnalysisResult {
  /** Version of the analyzer that produced this result */
  version: number
  /** Detection status for each story element */
  present: Record<StoryElement, DetectionStatus>
  /** Detailed notes about what was found */
  notes: AnalysisNote[]
  /** Kid-friendly suggestions for improvement */
  suggestions: Suggestion[]
}

/**
 * A note about a detected (or not detected) element
 */
export interface AnalysisNote {
  /** Which story element this note is about */
  element: StoryElement
  /** Detection status */
  status: DetectionStatus
  /** Confidence score 0-100 */
  confidence: number
  /** Which detection rule triggered this note */
  ruleId: string
  /** Text excerpt where the element was found */
  excerpt?: string
  /** Character position in the original text */
  position?: number
  /** Position as percentile (0-100) */
  percentile?: number
}

/**
 * A suggestion for improving the story
 */
export interface Suggestion {
  /** Which story element this suggestion addresses */
  element: StoryElement
  /** Kid-friendly message */
  message: string
  /** Priority level */
  priority: 'high' | 'medium' | 'low'
}

/**
 * Labels and descriptions for story elements (kid-friendly)
 */
export const STORY_ELEMENT_INFO: Record<
  StoryElement,
  { label: string; emoji: string; description: string }
> = {
  problem: {
    label: 'The Problem',
    emoji: '😰',
    description: 'Something goes wrong or creates a challenge',
  },
  goal: {
    label: 'The Goal',
    emoji: '🎯',
    description: 'What the character wants or needs',
  },
  obstacle: {
    label: 'The Obstacle',
    emoji: '🚧',
    description: 'Something that gets in the way',
  },
  turning_point: {
    label: 'The Turning Point',
    emoji: '⚡',
    description: 'When everything changes',
  },
  resolution: {
    label: 'The Resolution',
    emoji: '🌈',
    description: 'How things work out in the end',
  },
  change: {
    label: 'The Change',
    emoji: '💡',
    description: 'What the character learns or how they grow',
  },
}

/**
 * All story elements in typical narrative order
 */
export const STORY_ELEMENTS: StoryElement[] = [
  'problem',
  'goal',
  'obstacle',
  'turning_point',
  'resolution',
  'change',
]
